import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';
import { BLOG_POSTS } from '../utils/seoConstants';
import { generateArticleSchema, generateFAQSchema } from '../utils/schemaGenerators';
import { getBlogContent, hasFullContent } from '../data/blogContent';

/**
 * Individual Blog Post Page
 * Renders full content for articles with content, placeholder for others
 */
const BlogPost = () => {
  const { slug } = useParams();

  // Find the blog post by slug
  const post = BLOG_POSTS.find(p => p.slug === slug);

  // Redirect to blog index if post not found
  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  // Check if we have full content for this post
  const fullContent = getBlogContent(slug);
  const hasContent = hasFullContent(slug);

  // Use full content data if available, otherwise use placeholder data
  const articleData = fullContent || {
    title: post.title,
    description: post.description,
    datePublished: '2026-03-26T00:00:00Z',
    dateModified: '2026-03-26T00:00:00Z',
    author: '1crew3 Team',
    keyFacts: getKeyFacts(slug),
    definitionLead: post.description,
    faqs: generateFAQsForTopic(slug)
  };

  // Generate schemas
  const articleSchema = generateArticleSchema({
    title: articleData.title,
    description: articleData.description,
    slug: slug,
    datePublished: articleData.datePublished,
    dateModified: articleData.dateModified
  });

  const faqSchema = articleData.faqs && articleData.faqs.length > 0
    ? generateFAQSchema(articleData.faqs)
    : null;

  const schemas = faqSchema ? [articleSchema, faqSchema] : [articleSchema];

  return (
    <>
      <SEOHead
        title={articleData.title}
        description={articleData.description}
        canonicalUrl={`/blog/${slug}`}
        ogType="article"
        article={{
          publishedTime: articleData.datePublished,
          modifiedTime: articleData.dateModified,
          section: 'Yacht Crew Career'
        }}
        schema={schemas}
      />

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-gradient-to-br from-[#0d1b2a] to-[#1b3a5d] text-white py-12">
          <div className="max-w-4xl mx-auto px-4">
            <Breadcrumbs
              items={[
                { name: 'Blog', href: '/blog' },
                { name: articleData.title.substring(0, 30) + '...', href: `/blog/${slug}` }
              ]}
              className="text-gray-300 mb-6"
            />
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              {articleData.title}
            </h1>
            <div className="flex items-center gap-4 text-gray-300 text-sm">
              <span>Last updated: March 26, 2026</span>
              <span>•</span>
              <span>By {articleData.author || '1crew3 Team'}</span>
            </div>
          </div>
        </header>

        {/* Article Content */}
        <main className="max-w-4xl mx-auto px-4 py-12">
          <article className="bg-white rounded-xl shadow-sm p-8">
            {/* Key Facts Box */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8 rounded-r-lg">
              <h2 className="font-semibold text-blue-900 mb-3">Key Takeaways</h2>
              <ul className="space-y-2 text-blue-800">
                {articleData.keyFacts.map((fact, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span>{fact}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Definition Lead - First paragraph */}
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              {articleData.definitionLead}
            </p>

            {/* Full Content or Placeholder */}
            {hasContent && articleData.sections ? (
              <div className="prose prose-lg max-w-none">
                {articleData.sections.map((section, index) => (
                  <section key={section.id || index} className="mb-10">
                    <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
                      {section.title}
                    </h2>
                    <div
                      className="text-gray-600 [&>p]:mb-4 [&>table]:my-6 [&>h3]:text-xl [&>h3]:font-semibold [&>h3]:text-gray-800 [&>h3]:mt-6 [&>h3]:mb-3 [&_a]:text-blue-600 [&_a]:hover:text-blue-800"
                      dangerouslySetInnerHTML={{ __html: section.content }}
                    />
                  </section>
                ))}
              </div>
            ) : (
              <div className="prose prose-lg max-w-none text-gray-600">
                <p className="mb-6">
                  This comprehensive guide covers everything you need to know about {post.title.toLowerCase().replace(/—.*/, '').trim()}.
                  Whether you're just starting your yachting career or looking to advance,
                  you'll find valuable information and practical advice.
                </p>

                <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">
                  What You'll Learn
                </h2>
                <p className="mb-6">
                  Our detailed guide is being updated with the latest industry information.
                  Check back soon for comprehensive content including real-world examples,
                  expert insights, and actionable tips.
                </p>

                <div className="bg-gray-100 rounded-lg p-6 my-8">
                  <p className="text-center text-gray-500 italic">
                    Full article content coming soon. Sign up for updates!
                  </p>
                </div>
              </div>
            )}

            {/* FAQ Section */}
            {articleData.faqs && articleData.faqs.length > 0 && (
              <section className="mt-12 border-t border-gray-200 pt-10">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-6">
                  {articleData.faqs.map((faq, i) => (
                    <div key={i} className="border-b border-gray-200 pb-6 last:border-0">
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {faq.question}
                      </h3>
                      <p className="text-gray-600">
                        {faq.answer}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* CTA */}
            <div className="mt-12 p-8 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl text-center">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {articleData.ctaTitle || 'Ready to Find Your Next Yacht Job?'}
              </h3>
              <p className="text-gray-600 mb-6">
                {articleData.ctaText || 'Browse open positions or create your free crew profile today.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/jobs"
                  className="px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-lg transition-colors"
                >
                  Browse Jobs
                </Link>
                <Link
                  to="/register"
                  className="px-6 py-3 bg-white hover:bg-gray-50 text-gray-900 font-semibold rounded-lg border border-gray-200 transition-colors"
                >
                  Sign Up Free
                </Link>
              </div>
            </div>
          </article>

          {/* Related Articles */}
          <section className="mt-12">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              Related Articles
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {BLOG_POSTS
                .filter(p => p.slug !== slug)
                .slice(0, 3)
                .map((relatedPost) => (
                  <Link
                    key={relatedPost.slug}
                    to={`/blog/${relatedPost.slug}`}
                    className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                  >
                    <h3 className="font-medium text-gray-900 line-clamp-2 mb-2">
                      {relatedPost.title}
                    </h3>
                    <p className="text-sm text-gray-500 line-clamp-2">
                      {relatedPost.description}
                    </p>
                  </Link>
                ))}
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="bg-gray-900 text-gray-400 py-8">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <Link to="/" className="text-white font-bold text-lg">
              1crew3
            </Link>
            <p className="mt-2 text-sm">
              Connect. Match. Work.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

/**
 * Generate key facts for topics without full content
 */
function getKeyFacts(slug) {
  const facts = {
    'yacht-crew-salary-guide': [
      'Entry-level crew: €2,500–€3,500/month',
      'Mid-level crew (bosun, 2nd stew, 2nd engineer): €3,800–€7,000/month',
      'Senior crew (chief stew, head chef, chief engineer): €5,500–€15,000/month',
      'Captains: €8,000–€25,000+/month depending on yacht size',
      'Charter tips can add €1,000–€5,000+ per charter week'
    ],
    'how-to-become-yacht-crew': [
      'Minimum requirements: ENG1 medical + STCW Basic Safety Training',
      'Training cost: €1,500–€4,000 (basic to comprehensive course)',
      'Timeline: 1–3 months from training to first job',
      'No prior experience needed for entry-level roles',
      'Peak hiring: March–May (Med) and October–November (Caribbean)'
    ],
    'stcw-certification-guide': [
      'STCW (Standards of Training, Certification and Watchkeeping) is mandatory',
      'Basic STCW course takes 5-7 days to complete',
      'Cost ranges from €800-€1,500 depending on location',
      'Certification is valid for 5 years before renewal required',
      'Includes firefighting, sea survival, first aid, and safety training'
    ],
    'yacht-crew-cv-template': [
      'Yacht CVs should be 1-2 pages maximum',
      'Include a professional headshot at the top',
      'List certifications prominently',
      'Include sea time and vessel sizes worked on',
      'References are essential for yacht crew CVs'
    ],
    'best-yacht-crew-agencies': [
      'Online platforms like 1crew3 offer free crew registration',
      'Traditional agencies charge placement fees (crew or yacht)',
      'Most hires now happen through digital platforms',
      'Specialist agencies focus on specific positions or regions',
      'Direct networking remains highly effective'
    ]
  };

  return facts[slug] || [
    'This guide provides comprehensive industry information',
    'Based on current 2026 yacht industry standards',
    'Updated regularly with the latest data',
    'Written by yacht industry professionals'
  ];
}

/**
 * Generate FAQs for topics without full content
 */
function generateFAQsForTopic(slug) {
  const faqSets = {
    'yacht-crew-salary-guide': [
      { question: 'How much does a yacht deckhand earn?', answer: 'A yacht deckhand typically earns between €2,500 and €3,500 per month on yachts 30-50m, and €3,000 to €4,500 per month on superyachts over 50m. Entry-level deckhands start around €2,500/month.' },
      { question: 'Do yacht crew get tips?', answer: 'Yes, tips are a significant part of yacht crew compensation. Charter yachts typically receive tips of 10-20% of charter fee, which is split among the crew based on position and tenure.' },
      { question: 'What is the highest paying yacht crew position?', answer: 'Captain positions are the highest paid, ranging from €8,000 to €25,000+ per month depending on yacht size and experience. Large superyacht captains (80m+) can earn over €200,000 annually.' },
      { question: 'Is yacht crew salary tax-free?', answer: 'It depends on your residency and the yacht\'s flag state. Many crew members benefit from reduced tax burdens by establishing residency in tax-favorable jurisdictions and working on yachts flagged in certain countries.' }
    ],
    'how-to-become-yacht-crew': [
      { question: 'Do I need experience to work on a yacht?', answer: 'No prior yachting experience is required for entry-level positions like junior deckhand or junior stewardess. However, you will need STCW certification and an ENG1 medical certificate.' },
      { question: 'What qualifications do I need to work on a yacht?', answer: 'At minimum, you need STCW basic safety training and an ENG1 medical certificate. Additional certifications like Powerboat Level 2, food hygiene certificates, or specialized training improve your chances.' },
      { question: 'Where should I go to find my first yacht job?', answer: 'The main yachting hubs for finding work are Antibes (France), Fort Lauderdale (USA), and Palma de Mallorca (Spain). Walking the docks, registering on platforms like 1crew3, and networking are the best approaches.' },
      { question: 'How long does it take to get a job on a yacht?', answer: 'This varies greatly. Some crew find positions within days of arriving in a yachting hub, while others may search for several weeks. Persistence, flexibility, and good presentation are key.' }
    ],
    'stcw-certification-guide': [
      { question: 'What does STCW stand for?', answer: 'STCW stands for Standards of Training, Certification and Watchkeeping for Seafarers. It\'s an international convention that sets minimum qualification standards for merchant ship crews.' },
      { question: 'How long is STCW valid?', answer: 'STCW certificates are valid for 5 years. Before they expire, you must complete a refresher course (typically 1-2 days) to maintain certification.' },
      { question: 'How much does STCW certification cost?', answer: 'STCW basic safety training costs between €800-€1,500 depending on the training center location. UK and Mediterranean centers tend to be more expensive than those in Philippines or South Africa.' },
      { question: 'Can I work on a yacht without STCW?', answer: 'No, STCW certification is legally required for all crew working on commercially registered yachts. Some day work on private yachts may not require it, but for any professional position, it\'s mandatory.' }
    ]
  };

  return faqSets[slug] || [
    { question: `What is covered in this ${slug.replace(/-/g, ' ')} article?`, answer: 'This comprehensive guide covers all essential information, best practices, and industry insights relevant to the topic. Check back soon for the complete article.' },
    { question: 'Is this information up to date for 2026?', answer: 'Yes, all our guides are regularly updated to reflect current industry standards, salary data, and best practices in the yacht industry.' }
  ];
}

export default BlogPost;
